import { useEffect, useState } from "react";
import { Clock, Warning } from "@phosphor-icons/react";

/**
 * Live "N days left" ticker for habitability jobs. The Philly Safe Healthy Homes Act
 * gives landlords 30 days to remediate a habitability defect. Clock starts at the
 * job's `created_at` (when the tenant/office logged the issue).
 *
 * Visual states:
 *   - >14 days left → cyan (calm)
 *   - 7-14 days     → amber (watch)
 *   - 1-7 days      → red (pulse)
 *   - overdue       → black + red text (past deadline, keeps counting)
 */
export default function HabitabilityCountdown({ startAt, size = "sm", testId }) {
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 60_000); // tick every minute
    return () => clearInterval(t);
  }, []);

  if (!startAt) return null;
  const start = new Date(startAt).getTime();
  if (!Number.isFinite(start)) return null;

  const deadline = start + 30 * 24 * 60 * 60 * 1000;
  const msLeft = deadline - now;
  const daysLeft = Math.ceil(msLeft / (24 * 60 * 60 * 1000));
  const overdue = daysLeft < 0;

  let bg, label, pulse = false, Icon = Clock;
  if (overdue) {
    bg = "bg-[#0f172a] text-red-400 border-red-500";
    label = `OVERDUE +${Math.abs(daysLeft)}d`;
    pulse = true;
    Icon = Warning;
  } else if (daysLeft <= 7) {
    bg = "bg-red-600 text-white";
    label = `${daysLeft}d LEFT`;
    pulse = true;
    Icon = Warning;
  } else if (daysLeft <= 14) {
    bg = "bg-amber-500 text-white";
    label = `${daysLeft}d left`;
  } else {
    bg = "bg-cyan-600 text-white";
    label = `${daysLeft}d left`;
  }

  const sizing = size === "lg"
    ? "h-8 px-3 text-xs gap-2"
    : "h-5 px-1.5 text-[10px] gap-1";
  const iconSize = size === "lg" ? 14 : 10;

  return (
    <span
      data-testid={testId || "habitability-countdown"}
      title={`30-Day PHL clock · started ${new Date(startAt).toLocaleDateString()} · deadline ${new Date(deadline).toLocaleDateString()}`}
      className={`inline-flex items-center font-black uppercase tracking-widest rounded-md border ${sizing} ${bg} ${pulse ? "animate-pulse" : ""}`}>
      <Icon weight="fill" size={iconSize}/> {label}
    </span>
  );
}
